import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle, InfoIcon, Mail } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

interface NotificationSettingsProps {
  onSave: () => void;
}

interface NotificationPreferences {
  id: string;
  admin_id: string;
  notify_on_document_events: boolean;
  notify_on_certificate_events: boolean;
  notify_on_compliance_events: boolean;
  notify_on_retention_events: boolean;
  ui_notifications: boolean;
  email_notifications: boolean;
  created_at: string;
  updated_at: string;
}

export function NotificationSettings({ onSave }: NotificationSettingsProps) {
  const { adminUser } = useAdminAuth();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const fetchPreferences = async () => {
      if (!adminUser?.id) return;
      
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('admin_notification_preferences')
          .select('*')
          .eq('admin_id', adminUser.id)
          .single();
        
        if (error) throw error;
        setPreferences(data);
      } catch (error) {
        console.error('Error fetching notification preferences:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreferences();
  }, [adminUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminUser?.id || !preferences) return;
    
    try {
      setIsSubmitting(true);
      
      // If no preferences exist yet, create them
      if (!preferences.id) {
        const { error } = await supabase
          .from('admin_notification_preferences')
          .insert({
            admin_id: adminUser.id,
            notify_on_document_events: preferences.notify_on_document_events,
            notify_on_certificate_events: preferences.notify_on_certificate_events,
            notify_on_compliance_events: preferences.notify_on_compliance_events,
            notify_on_retention_events: preferences.notify_on_retention_events,
            ui_notifications: preferences.ui_notifications,
            email_notifications: preferences.email_notifications
          });
          
        if (error) throw error;
      } else {
        // Otherwise update existing preferences
        const { error } = await supabase
          .from('admin_notification_preferences')
          .update({
            notify_on_document_events: preferences.notify_on_document_events,
            notify_on_certificate_events: preferences.notify_on_certificate_events,
            notify_on_compliance_events: preferences.notify_on_compliance_events,
            notify_on_retention_events: preferences.notify_on_retention_events,
            ui_notifications: preferences.ui_notifications,
            email_notifications: preferences.email_notifications,
            updated_at: new Date().toISOString()
          })
          .eq('id', preferences.id);
          
        if (error) throw error;
      }
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      onSave();
    } catch (error) {
      console.error('Error saving notification preferences:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePreference = (key: keyof NotificationPreferences) => {
    if (!preferences) return;
    setPreferences(prev => {
      if (!prev) return prev;
      return { ...prev, [key]: !prev[key] };
    });
  };

  // Initialize default preferences if none exist
  useEffect(() => {
    if (!isLoading && !preferences && adminUser?.id) {
      setPreferences({
        id: '',
        admin_id: adminUser.id,
        notify_on_document_events: true,
        notify_on_certificate_events: true,
        notify_on_compliance_events: true,
        notify_on_retention_events: true,
        ui_notifications: true,
        email_notifications: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
    }
  }, [isLoading, preferences, adminUser]);
  
  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">Loading notification preferences...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
          <CardDescription>
            Choose which notifications you want to receive and how
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {showSuccess && (
              <Alert className="bg-green-50 border-green-200 mb-4">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-800">Success</AlertTitle>
                <AlertDescription className="text-green-700">
                  Your notification preferences have been saved.
                </AlertDescription>
              </Alert>
            )}
            
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-4">Notification Channels</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>UI Notifications</Label>
                    <div className="text-sm text-muted-foreground">
                      Receive notifications in the application interface
                    </div>
                  </div>
                  <Switch
                    checked={preferences?.ui_notifications || false}
                    onCheckedChange={() => togglePreference('ui_notifications')}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Email Notifications</Label>
                    <div className="text-sm text-muted-foreground">
                      Receive notifications via email at {adminUser?.email}
                    </div>
                  </div>
                  <Switch 
                    checked={preferences?.email_notifications || false}
                    onCheckedChange={() => togglePreference('email_notifications')}
                  />
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-4">Notification Types</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Certificate Events</Label>
                    <div className="text-sm text-muted-foreground">
                      Notifications about certificate issuance, expiration, etc.
                    </div>
                  </div>
                  <Switch 
                    checked={preferences?.notify_on_certificate_events || false}
                    onCheckedChange={() => togglePreference('notify_on_certificate_events')}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Compliance Events</Label>
                    <div className="text-sm text-muted-foreground">
                      Notifications about compliance deadlines, reports, etc.
                    </div>
                  </div>
                  <Switch 
                    checked={preferences?.notify_on_compliance_events || false}
                    onCheckedChange={() => togglePreference('notify_on_compliance_events')}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Document Events</Label>
                    <div className="text-sm text-muted-foreground">
                      Notifications about document uploads, approvals, etc.
                    </div>
                  </div>
                  <Switch 
                    checked={preferences?.notify_on_document_events || false}
                    onCheckedChange={() => togglePreference('notify_on_document_events')}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Retention Events</Label>
                    <div className="text-sm text-muted-foreground">
                      Notifications about data retention policy execution
                    </div>
                  </div>
                  <Switch 
                    checked={preferences?.notify_on_retention_events || false}
                    onCheckedChange={() => togglePreference('notify_on_retention_events')}
                  />
                </div>
              </div>
            </div>
            
            <Alert variant="default" className="bg-blue-50 border-blue-200">
              <InfoIcon className="h-4 w-4 text-blue-600" />
              <AlertTitle className="text-blue-800">Email Configuration</AlertTitle>
              <AlertDescription className="text-blue-700">
                Emails are sent via Resend from <strong>notifications@drivecourses.com</strong>. 
                Email settings can be managed in the <a href="/settings?tab=integrations" className="underline font-medium">Integrations tab</a>.
              </AlertDescription>
            </Alert>
            
            <div className="flex justify-end">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Preferences'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
